<!-- "+ New project": a button that acts in place (opens a dialog) instead of navigating -->
<button
  *ngIf="isAction(item)"
  (click)="run(item)"
  type="button"
  class="vex-sidenav-action"
  [attr.aria-label]="item.label"
  [matTooltip]="item.label"
  matTooltipPosition="right"
  [matTooltipDisabled]="!collapsed">
  <i *ngIf="item.icon" [class]="'tan-icon-d vex-sidenav-action__icon ' + item.icon" aria-hidden="true"></i>
  <span class="vex-sidenav-action__label">{{ item.label }}</span>
</button>

<ng-container *ngIf="isLink(item) && !isFunction(item.route) && item.disabled !== true">
  <!-- On the folded rail an item with a card shows the card; one without shows its label as a tooltip.
       It is also lit when the page shown is one of its card rows (Team on Access & roles). -->
  <a
    #linkEl
    cdkOverlayOrigin
    #cardOrigin="cdkOverlayOrigin"
    [fragment]="item.fragment"
    [queryParams]="item.queryParams"
    [state]="item.state"
    [routerLinkActiveOptions]="item.routerLinkActiveOptions || { exact: false }"
    [routerLink]="item.unavailable? '' : item.route"
    class="vex-sidenav-item group"
    [class.unavailable]="item.unavailable"
    [class.vex-sidenav-item--card-open]="cardOpen"
    [class.vex-sidenav-item--active-card]="!!activeRow"
    [matTooltip]="item.label"
    matTooltipPosition="right"
    [matTooltipDisabled]="!collapsed || !!item.card"
    [attr.aria-haspopup]="item.card ? 'true' : null"
    [attr.aria-expanded]="item.card ? cardOpen : null"
    (mouseenter)="onItemEnter()"
    (mouseleave)="onLeave()"
    (focus)="onItemFocus()"
    (blur)="onItemBlur($event)"
    (keydown.arrowright)="focusCard($event)"
    (keydown.escape)="closeCard()"
    matRipple
    matRippleColor="var(--vex-sidenav-item-ripple-color)"
    routerLinkActive="vex-sidenav-item--active">
    <i *ngIf="level === 0 && item.icon" [class]="'tan-icon-d bg-white/70 ' + item.icon"></i>
    <span class="vex-sidenav-item__label">{{ item.label }}</span>@if(item.beta){<small>BETA</small>}
    <span
      *ngIf="item.planned"
      class="vex-sidenav-item__planned"
      [matTooltip]="item.planned"
      matTooltipPosition="right">Planned</span>
    <span
      *ngIf="item.badge"
      [ngClass]="[item.badge.bgClass, item.badge.textClass]"
      class="vex-sidenav-item__badge"
    >{{ item.badge.value }}</span
    >
    <mat-icon
      *ngIf="item.card"
      class="vex-sidenav-item__chevron"
      svgIcon="mat:keyboard_arrow_right"
      aria-hidden="true"></mat-icon>
  </a>

  <!-- Search: the sub-pages that matched, listed under their item -->
  <div *ngIf="item.searchHits?.length" class="vex-sidenav-item__hits">
    <ng-container *ngFor="let row of item.searchHits">
      <a
        *ngIf="row.route; else plannedHit"
        [routerLink]="row.route"
        [queryParams]="row.queryParams"
        [state]="row.state"
        class="vex-sidenav-item__hit">
        <span class="vex-sidenav-item__label">{{ row.label }}</span>
        <span *ngIf="row.planned" class="vex-sidenav-item__planned" [matTooltip]="row.planned" matTooltipPosition="right">Planned</span>
      </a>
      <ng-template #plannedHit>
        <span class="vex-sidenav-item__hit vex-sidenav-item__hit--static" tabindex="0" [matTooltip]="row.planned || ''" matTooltipPosition="right">
          <span class="vex-sidenav-item__label">{{ row.label }}</span>
          <span class="vex-sidenav-item__planned">Planned</span>
        </span>
      </ng-template>
    </ng-container>
  </div>

  <!-- The sub-page card, beside the item (hover or keyboard focus) -->
  <ng-template
    *ngIf="item.card"
    cdkConnectedOverlay
    [cdkConnectedOverlayOrigin]="cardOrigin"
    [cdkConnectedOverlayOpen]="cardOpen"
    [cdkConnectedOverlayPositions]="cardPositions"
    [cdkConnectedOverlayPush]="true"
    (detach)="closeCard()">
    <div
      #cardPanel
      class="vex-sidenav-card"
      role="menu"
      [attr.aria-label]="item.card.title"
      (mouseenter)="onCardEnter()"
      (mouseleave)="onCardLeave()"
      (focusout)="onCardFocusOut($event)"
      (keydown.escape)="closeCardToItem($event)"
      (keydown.arrowleft)="closeCardToItem($event)">
      <div class="vex-sidenav-card__title">{{ item.card.title }}</div>
      <ng-container *ngFor="let group of item.card.groups">
        <div *ngIf="group.label" class="vex-sidenav-card__group">{{ group.label }}</div>
        <ng-container *ngFor="let row of visibleRows(group.links)">
          <a
            *ngIf="row.route; else plannedRow"
            role="menuitem"
            class="vex-sidenav-card__row"
            [class.vex-sidenav-card__row--active]="row === activeRow"
            [attr.aria-current]="row === activeRow ? 'page' : null"
            [routerLink]="row.route"
            [queryParams]="row.queryParams"
            [state]="row.state"
            (click)="onCardRowClick()">
            <span class="vex-sidenav-card__label">{{ row.label }}</span>
            <span *ngIf="row.planned" class="vex-sidenav-item__planned" [matTooltip]="row.planned" matTooltipPosition="right">Planned</span>
            <span
              *ngIf="rowBadge(row) as badge"
              [ngClass]="[badge.bgClass, badge.textClass]"
              class="vex-sidenav-card__badge">{{ badge.value }}</span>
          </a>
          <ng-template #plannedRow>
            <!-- Planned with no page yet: not clickable, the tooltip says why -->
            <span
              role="menuitem"
              aria-disabled="true"
              tabindex="0"
              class="vex-sidenav-card__row vex-sidenav-card__row--static"
              [matTooltip]="row.planned || ''"
              matTooltipPosition="right">
              <span class="vex-sidenav-card__label">{{ row.label }}</span>
              <span class="vex-sidenav-item__planned">Planned</span>
            </span>
          </ng-template>
        </ng-container>
      </ng-container>
    </div>
  </ng-template>
</ng-container>

<div
  (click)="item.route()"
  *ngIf="isLink(item) && isFunction(item.route) && item.disabled !== true"
  class="vex-sidenav-item"
  [class.unavailable]="item.unavailable"
  matRipple
  matRippleColor="var(--vex-sidenav-item-ripple-color)"
  routerLinkActive="vex-sidenav-item--active">
  <i *ngIf="level === 0 && item.icon" [class]="'tan-icon-d bg-white/70 ' + item.icon"></i>
  <span class="vex-sidenav-item__label">{{ item.label }}</span>
  <span
    *ngIf="item.badge"
    [ngClass]="[item.badge.bgClass, item.badge.textClass]"
    class="vex-sidenav-item__badge"
  >{{ item.badge.value }}</span>
</div>

<ng-container *ngIf="isDropdown(item) && visibleContainer(item)">
  <!-- Abel 12/9/24: añado class.unavailable para mostrar los dropdowns que aún no estan disponibles -->
  <div
    (click)="toggleOpen()"
    [class.vex-sidenav-item--active]="isOpen || isActive"
    [class.vex-sidenav-item--open]="isOpen"
    [class.unavailable]="item.unavailable"
    class="vex-sidenav-item"
    matRipple
    matRippleColor="var(--vex-sidenav-item-ripple-color)">
    <i *ngIf="level === 0 && item.icon" [class]="'tan-icon-d bg-white/70 ' + item.icon"></i>
    <span class="vex-sidenav-item__label">{{ item.label }}</span>
    <span
      *ngIf="item.badge"
      [ngClass]="[item.badge.bgClass, item.badge.textClass]"
      class="vex-sidenav-item__badge"
    >{{ item.badge.value }}</span
    >
    <mat-icon
      class="vex-sidenav-item__dropdown-icon"
      svgIcon="mat:keyboard_arrow_right"></mat-icon>
  </div>
  <div [@dropdown]="isOpen" class="vex-sidenav-item__dropdown">
    <vex-sidenav-item
      *ngFor="let subItem of item.children; trackBy: trackChild"
      [item]="subItem"
      [level]="level + 1"></vex-sidenav-item>
  </div>
</ng-container>

<ng-container *ngIf="isSubheading(item)">
  <!-- The first group has no heading in the design -->
  <div *ngIf="item.label" class="vex-sidenav-subheading">{{ item.label }}</div>
  <vex-sidenav-item
    *ngFor="let subItem of item.children; trackBy: trackChild"
    [item]="subItem"
    [level]="0"></vex-sidenav-item>
</ng-container>
